import { isNil } from 'lodash';

export const toOptionalString = (
  value: string | number | undefined
): string | undefined => (isNil(value) ? undefined : String(value));

export const formatNumber = (
  num: number | undefined,
  maximumSignificantDigits = 2
): string => {
  if (num || num === 0)
    return num.toLocaleString('en-US', {
      maximumSignificantDigits,
    });
  return '';
};

export const usdFormatter = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const usdFormatterNoCents = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export const formatDollars = (num: number): string => {
  // format using usdFormatter, rounded dollars with no cents
  if (!num) return '';
  return usdFormatterNoCents.format(num);
};

export const formatPercentage = (decimal: number): string => {
  // format decimal number as percentage, e.g. 0.5 => 50, 0.6666 => 67
  const percentage = (decimal * 100).toFixed(0);
  return `${percentage}%`;
};

export const formatPercentageWithParentheticalNegatives = (
  decimal: number
): string => {
  const percentage = Math.abs(decimal * 100).toFixed(0);
  if (decimal < 0) {
    return `(${percentage}%)`;
  }
  return `${percentage}%`;
};

export function stripNonNumeric(value: string | null): string | null {
  if (!value) return value;
  const digits = value.replace(/\D/g, '');
  return digits || null;
}

// NOTE(dev): FMCSA docket prefixes that carry meaning and must not be
// stripped. MX = Mexican carrier, FF = Freight Forwarder,
// LC = Livestock Carrier, A = Broker/Forwarder authority.
const FMCSA_DOCKET_PREFIX = /^(MX|FF|LC|A)\d/i;

export function normalizeMcNumber(value: string | null): string | null {
  if (!value) return value;
  if (FMCSA_DOCKET_PREFIX.test(value)) return value;
  return stripNonNumeric(value);
}

export const DIGIT_TO_WORD: Record<string, string> = {
  '0': 'zero',
  '1': 'one',
  '2': 'two',
  '3': 'three',
  '4': 'four',
  '5': 'five',
  '6': 'six',
  '7': 'seven',
  '8': 'eight',
  '9': 'nine',
};

export function expandDigitWords(transcript: string): string {
  return transcript.replace(/\d+/g, (match) => {
    if (match.length === 1 || match.length > 3) {
      return match
        .split('')
        .map((digit) => DIGIT_TO_WORD[digit] ?? digit)
        .join(' ');
    }
    return match; // Keep 2- and 3-digit numbers unchanged
  });
}
